import { ForemanJobDetail, ForemanPhase } from '@ups/xplat/api/dto';

/**
 * The offline job cache merges remote data with cached priority
 * (`mergeObjectsCachedPriorityMut`), so once a work order (phase) row is cached,
 * a refreshed value from the server is discarded. `udTimesheetBlock` is the
 * Viewpoint per-phase timesheet block (`bJCJP.udTimesheetBlock`, `'Y'` = blocked,
 * #30262), office/VP-owned and never foreman-edited. When a phase is cached
 * BEFORE Viewpoint blocks it, the stale cached `udTimesheetBlock` survives every
 * job refresh, so `isWorkOrderTimesheetBlocked` keeps treating it as open, the
 * entry picker keeps offering it, and the submit then fails the server block
 * guard (`ForemanSubmitTimeRepository.EnsureNoNewTimeOnBlockedPhases`) with
 * "Time cannot be added to work order(s) ...: blocked for timesheet entry in
 * Viewpoint" (Bug 32135).
 *
 * Overlay the remote `udTimesheetBlock` onto every merged work order so the
 * current block state reaches the device (both directions: a newly blocked phase
 * becomes blocked, and a phase VP has since unblocked reopens). Work orders with
 * no matching remote row (foreman-created, not yet synced) are left untouched.
 */
export function reconcileTimesheetBlockFromRemoteJob(
  mergedJob: ForemanJobDetail,
  remoteJob: ForemanJobDetail
): void {
  const remoteWorkOrderById = new Map(
    (remoteJob.workOrders || []).map(
      (wo) => [String(wo.foremanPhaseID ?? ''), wo] as const
    )
  );

  for (const wo of mergedJob.workOrders || []) {
    const id = String(wo.foremanPhaseID ?? '');
    const remote: ForemanPhase | undefined = id
      ? remoteWorkOrderById.get(id)
      : undefined;
    if (!remote) {
      continue;
    }

    // Office/VP-owned field: the repository always maps it to a concrete flag
    // ('Y' / 'N' / ''), so a null/undefined remote value means the row was not
    // mapped, not a real unblock. Overlaying it would blank a genuine block on a
    // mapping gap, so only carry a concrete value across (an explicit '' still
    // reads as open via isWorkOrderTimesheetBlocked, preserving the unblock path).
    if (remote.udTimesheetBlock != null) {
      wo.udTimesheetBlock = remote.udTimesheetBlock;
    }
  }
}
